/**
 * Secrets that arrive in a URL (emailed links, sign-in and payment returns) never stay in the
 * address bar, where Google Tag Manager, gtag and GA read them (history change events copy the
 * URL and history.state too). The first scripts in public/index.html run before any tag loads:
 * the wallet guard takes a wallet link's `t`, and the URL secret guard (<script
 * id="url-secret-guard">) takes, on every path:
 *   - from the query:    token (reset, verify, preference and invite links), email (the
 *                        emailed "Set a password" links), session_id (Stripe's return), invite
 *                        (the business invitation sign-up link)
 *   - from the fragment: t (except on /deal-wallet, which the wallet guard owns), token,
 *                        session_id (the Google / Emergent sign-in return)
 * Each value goes to sessionStorage under `tappd:url-secret:<part>:<name>` as
 * `{"v": value, "p": pathname}` (the path it arrived on), and the URL is replaced with the
 * value removed (history.replaceState with null state). If storage fails, nothing is removed
 * and the page reads the value from the URL as before.
 *
 * Pages read a value with readUrlSecret: the live URL first (a tab opened before the guard, an
 * in-app link, the tests), else the stash for the current path. Reading never clears (a
 * re-render, StrictMode's double calls and a reload all find it again). The page clears it
 * with clearUrlSecret once the value has been used: when the action it serves has finished
 * (password set, email verified, invitation accepted, Google sign-in exchanged, Stripe return
 * handled, reset link sent, preferences saved). Anything left over dies with the tab and is
 * readable only on the path it came from. In-app handoffs (the sign-in dialog, the claim box
 * and the wallet send an address to /forgot-password) write the stash themselves with
 * stashUrlSecret, so the address is in neither the URL nor history.state.
 *
 * URL_SECRET_PREFIX and URL_SECRET_NAMES must equal the guard's PREFIX and NAMES
 * (src/__tests__/urlSecretGuard.test.js checks both).
 */

export const URL_SECRET_PREFIX = 'tappd:url-secret:';

/** The names the guard takes out of the address bar, per part of the URL. */
export const URL_SECRET_NAMES = Object.freeze({
  search: Object.freeze(['token', 'email', 'session_id', 'invite']),
  hash: Object.freeze(['t', 'token', 'session_id']),
});

const PARTS = ['search', 'hash'];
const partOf = (part) => (PARTS.includes(part) ? part : 'search');

/** The sessionStorage key of one stashed value. */
export const urlSecretKey = (name, part = 'search') => `${URL_SECRET_PREFIX}${partOf(part)}:${String(name).toLowerCase()}`;

/** "/Reset-Password/" and "/reset-password" are the same page (React Router ignores case). */
const samePath = (a, b) => {
  const norm = (p) => (String(p || '/').toLowerCase().replace(/\/+$/, '') || '/');
  return norm(a) === norm(b);
};

const currentLocation = () => (typeof window !== 'undefined' && window.location ? window.location : { pathname: '/', search: '', hash: '' });

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null; // access itself throws when storage is blocked
  }
};

/** The stashed value of `name` for `pathname`, or null (none, another path, unreadable). */
export function readStashedUrlSecret(name, { part = 'search', pathname } = {}) {
  try {
    const s = storage();
    const raw = s ? s.getItem(urlSecretKey(name, part)) : null;
    if (raw === null || raw === undefined) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry.v !== 'string') return null;
    return samePath(entry.p, pathname ?? currentLocation().pathname) ? entry.v : null;
  } catch {
    return null;
  }
}

/**
 * The value of `name` for the current page: the live URL first, else the stash for this path;
 * null when neither has it. Never clears (see clearUrlSecret).
 *
 * @param {string} name   e.g. 'token', 'email', 'session_id'
 * @param {{ part?: 'search'|'hash', location?: { pathname: string, search?: string, hash?: string } }} [options]
 *   `location`: the router's location (default window.location)
 * @returns {string|null}
 */
export function readUrlSecret(name, { part = 'search', location } = {}) {
  const loc = location || currentLocation();
  const p = partOf(part);
  const raw = String((p === 'hash' ? loc.hash : loc.search) || '').replace(/^[?#]/, '');
  const live = raw ? new URLSearchParams(raw).get(name) : null;
  if (live !== null) return live;
  return readStashedUrlSecret(name, { part: p, pathname: loc.pathname });
}

/** Forget a stashed value (the value has been used). Safe when there is none. */
export function clearUrlSecret(name, { part = 'search' } = {}) {
  try {
    const s = storage();
    if (s) s.removeItem(urlSecretKey(name, part));
  } catch {
    // storage unavailable: nothing was kept
  }
}

/**
 * Keep a value for the page at `pathname` (an in-app handoff), in the guard's format.
 * → true when stored (false: storage unavailable).
 */
export function stashUrlSecret(name, value, { part = 'search', pathname } = {}) {
  try {
    const s = storage();
    if (!s) return false;
    const key = urlSecretKey(name, part);
    const entry = JSON.stringify({ v: String(value), p: pathname ?? currentLocation().pathname });
    s.setItem(key, entry);
    return s.getItem(key) === entry;
  } catch {
    return false;
  }
}
